import {
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { AccordionComponent, ButtonComponent, ListTableComponent, PopupDialogComponent } from '@ppl/ui';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
} from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { AllowListAuthStore } from '../../../../../shared/authorization/allow-list-auth.store';
import {
  MEMBER_VIEW_PAGE_IDS,
  TABS,
} from '../../../../../shared/constants/page.constants';
import { STATIC_DATA } from '../../../../../shared/constants/static-data.constants';
import { TableRowActions } from '../../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../../shared/interfaces/list.interface';
import { NgZorroModule } from '../../../../../shared/modules/ng-zorro/ng-zorro.module';
import {
  DialogConfig,
  DialogService,
} from '../../../../../shared/services/dialog.service';
import { LookupService } from '../../../../../services/lookup.service';
import { TimesheetService } from '../../../../../services/timesheet.service';
import { ListService } from '../../../../../shared/services/list.service';
import { UserService } from '../../../../../services/user.service';
import { AddInvoiceComponent } from "./add-invoice/add-invoice.component";
import { LeadDetailsService } from '../../../../../services/lead-details.service';
import { InvoiceProviderProcessedListComponent } from "./invoice-provider-processed-list/invoice-provider-processed-list.component";
import { InvoiceService } from 'src/app/services/invoice.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { SharedDataService } from 'src/app/shared/services/shared-data-service';
import { DateTimeFormatterService } from '../../../../../shared/utils/date-time-formatter.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'ppl-provider-invoice',
  standalone: true,
  imports: [
    AccordionComponent,
    ListTableComponent,
    NgZorroModule,
    CommonModule,
    ButtonComponent,
    NzSelectModule,
    FormsModule,
    NzFormModule,
    ReactiveFormsModule,
    AddInvoiceComponent,
    InvoiceProviderProcessedListComponent,
    PopupDialogComponent
  ],
  templateUrl: './provider-invoice.component.html',
  styleUrl: './provider-invoice.component.css',
})
export class ProviderInvoiceComponent implements OnInit {
  sharedDataKey: string = STATIC_DATA.SHARED_REFERRAL_DATA;
  pageId = '';
  columnsToBeHidden: string[] = [];
  actionsToBeHidden: string[] = [];
  isPageRestrictionLoaded = false;
  isReadOnly = false;
  isSectionEditable = false;
  canApproveReject = true;
  isImportAllowed = false;
  actionButtons: TableRowActionsModel = new TableRowActions({ view: true });
  @ViewChild('addInvoiceDialog') addInvoiceDialog!: TemplateRef<any>;
  private dialogSvc = inject(DialogService);
  tabId: string = TABS.TIMESHEET;
  programName!: string;
  providerId!: number;
  consumers: any[] = [];
  inProgressInvoices: any[] = [];
  addDialogId: string = '';
  hasInvoiceChanged: boolean = false;
  mode: 'add' | 'edit' | 'view' = 'add';
  providerData: any;
  invoiceId!: number;
  selectedRow: any;
  shoInvoiceImportDialog: boolean = false;
  selectedInvoiceFile?: File;

  // Tags the Service Dates cell so its comma separated value can render one date per line.
  serviceDatesCellClassResolver = (_rowData: any, col: any): string =>
    col?.field === 'serviceDates' ? 'service-dates-cell' : '';

  constructor(
    private fb: FormBuilder,
    private lookupService: LookupService,
    private timesheetService: TimesheetService,
    private listService: ListService,
    private userService: UserService,
    private leadDetailsService: LeadDetailsService,
    private allowListStore: AllowListAuthStore,
    private invoiceService: InvoiceService,
    private notificationService: NotificationService,
    private sharedDataService: SharedDataService,
    private dateTimeFormatter: DateTimeFormatterService
  ) { }

  ngOnInit(): void {
    this.pageId = MEMBER_VIEW_PAGE_IDS[this.userService.getFirstSegment()!];
    const selectedProgram = this.userService.getSelectedProgram();
    this.providerId = JSON.parse(this.userService.getSelectedLead()!);
    this.programName = selectedProgram?.program_code ?? '';
    
    // hardcode check for NJPPP we can move this code to config file.
    if (this.programName === 'NJPPP'){
      this.isImportAllowed = true;
    }

    const userDetails = this.userService.getUserDetails();
    if (!userDetails) {
      console.error('no user details found');
      return;
    }

    this.setPageAccess();
    this.getConsumers();
    this.getInitData();
    this.leadDetailsService.getUpdatedLeadData().subscribe((data) => {
      const leadData = data?.responsedata;

      if (leadData) {
        const email = leadData.communicationData?.contactMethods?.find((comm: any) => comm.contactMethod === 'email')?.value || '';
        const phone = leadData.communicationData?.contactMethods?.find((comm: any) => comm.contactMethod === 'mobile')?.value || '';
        const addressObj = leadData.communicationData?.permanentAddress || {};
        const address = [
          addressObj.street1,
          addressObj.street2,
          addressObj.city,
          addressObj.state,
          addressObj.zipCode
        ].filter(Boolean).join(', ');

        this.providerData = {
          name: leadData.infoData?.fname + ' ' + leadData.infoData?.lname,
          phone: phone,
          email: email,
          address: address,
          id: leadData.infoData?.providerId,
          providerPplId: leadData.infoData?.providerPPLId
        }
      }
    })
  }

  getInitData(startingPageNumber = 1, recordsPerPage = 10) {
    const inProcessPayload = this.buildRequestPayload(
      'invoice-provider-inProcess',
      'id',
      startingPageNumber,
      recordsPerPage
    );

    this.listService
      .getListData(inProcessPayload, 'claims')
      .subscribe((data: any) => {
        const records = data.responsedata ?? [];
        // Accumulate pages so the grid can offer a probe page and detect the
        // end of an unknown-total list. Sort the merged set, not just this page.
        const preparedRecords = this.prepareInvoices(records);
        this.inProgressInvoices = startingPageNumber > 1
          ? this.filterRecords([...this.inProgressInvoices, ...preparedRecords])
          : this.filterRecords(preparedRecords);
      });
  }

  onInProcessInvoicePageLoad(event: any) {
    this.getInitData(event.startingPageNumber ?? 1, event.rows ?? 10);
  }

  buildRequestPayload(pageName: string, orderBy: string, startingPageNumber = 1, recordsPerPage = 10) {
    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: [
        {
          jsonPath: 'providerId',
          value: this.providerId,
          operator: 'Equal',
        },
      ],
      orderByColumn: [orderBy],
      orderBy: ['desc'],
    };
    return payload;
  }

  getConsumers() {
    this.timesheetService
      .getConsumerList(this.programName, this.providerId)
      .subscribe((data: any) => {
        this.consumers = data?.responsedata ?? [];
      });
  }

  /**
   * Turns the API's yyyy-MM-dd serviceDate into the MM/dd/yyyy serviceDates value the
   * Service Dates column binds to, one date per line.
   */
  private prepareInvoices(data: any[]): any[] {
    return (data ?? []).map((invoice: any) => ({
      ...invoice,
      serviceDates: this.dateTimeFormatter.formatServiceDateList(invoice.serviceDate)
    }));
  }

  filterRecords(data: any) {
    data.sort((a: any, b: any) => {
      const dateA = new Date(a.fromDate);
      const dateB = new Date(b.fromDate);
      return dateA.getTime() - dateB.getTime(); // Sorts in ascending order
    });

    return data;
  }

  onAction(event: any, type: string) {
    if (event.action === 'view') {
      this.invoiceId = event.data?.invoiceHeaderId;
      this.selectedRow = event.data;
      this.openInvoice('view');
    }
  }

  handleCancel(event: any) {
    if (((Array.isArray(event) && event[0]?.invoiceHeaderId) || event?.isCloseTriggeredByDialog) && this.addDialogId) {
      this.dialogSvc.closeDialog(this.addDialogId);
      if (this.hasInvoiceChanged) {
        this.getInitData();
        this.hasInvoiceChanged = false;
      }
      this.addDialogId = '';
      this.invoiceId = null!;
      this.selectedRow = null;

      this.getInitData();
    }
  }

  openInvoice(mode: 'add' | 'edit' | 'view') {
    this.mode = mode;
    const dialogConfig: DialogConfig = {
      title: mode === 'add' ? 'Add Invoice' : 'View Invoice',
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.addDialogId = this.dialogSvc.openDialog(
      this.addInvoiceDialog,
      dialogConfig,
      () => this.handleCancel({ isCloseTriggeredByDialog: true }),
      () => this.handleCancel({ isCloseTriggeredByDialog: true })
    );
  }

  private setPageAccess() {
    const pageConfig = this.allowListStore.getConfig()?.pages[this.pageId];
    if (pageConfig) {
      const allowedActions = this.allowListStore.getAllowedActions(this.pageId);
      const allowedButtons = this.allowListStore.getAllowedButtons(this.pageId);
      this.isReadOnly = this.isReadOnly || (allowedActions.length === 0 && allowedButtons.length === 0);
      this.isSectionEditable = this.allowListStore.isSectionEditable(this.pageId, 'invoice', 'Invoice');
    } else {
      this.isReadOnly = true;
      this.isSectionEditable = false;
    }
    this.canApproveReject = this.allowListStore.isButtonAllowed(this.pageId, 'approveReject');
    this.isPageRestrictionLoaded = true;
  }

  resetFileInput(input: HTMLInputElement): void {
    input.value = '';
  }

  openInvoiceImportDialog() {
    this.selectedInvoiceFile = undefined;
    this.shoInvoiceImportDialog = true;
  }

  downloadInvoiceTemplate(): void {
    fetch('assets/templates/ca_sdp_invoice_upload.xlsx')
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = this.programName + '_ca_sdp_invoice_upload.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  onInvoiceFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedInvoiceFile = input.files[0];
    }
  }

  importInvoiceFile(): void {
    if (!this.selectedInvoiceFile) return;
    
    const formData = new FormData();
    formData.append('file', this.selectedInvoiceFile as any);
    formData.append('providerId', this.providerId.toString());

    this.invoiceService.importInvoice(formData, this.programName).subscribe({
      next: () => {
        this.shoInvoiceImportDialog = false;
        this.selectedInvoiceFile = undefined;
      },
      error: () => {
        this.notificationService.error('Failed', 'Failed to Import');
      }
    });
  }

}
